import { ActionIcon, Group, Modal, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCircleCheckFilled, IconCircleXFilled, IconTrash } from "@tabler/icons-react";
import { DataTable } from "mantine-datatable";
import { sortBy } from "ramda";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dateTimeToString } from "../../../../../utils/dateTimeUtils";
import { CALENDAR_TYPES } from "../../../../constants/calendarConstants";
import { COLORS } from "../../../../constants/colorConstants";
import { useCalendarContext } from "../../../../contexts/CalendarContext";
import { useLocalAuthorityContext } from "../../../../contexts/LocalAuthorityContext";
import useAuth from "../../../../hooks/core/useAuth";
import CopyIdButton from "../../../../shared/components/ui/Buttons/CopyIdButton";
import DataTablePaginationSection from "../../../../shared/components/ui/Tables/DataTablePaginationSection";
import { getLabelById } from "../../../../utils/objectUtils";
import { canDeleteCalendar } from "../utils/permissionsUtils";
import BulkActionsToolbar from "./BulkActionsToolbar";
import CalendarDeletionDialog from "./CalendarDeletionDialog";

const PAGE_SIZES = [10, 25, 50, 100, 200, 1000];

export default function CalendarTable() {
  const { tableFetch } = useCalendarContext();
  const calendars = tableFetch?.data?.calendars || [];

  const [pageSize, setPageSize] = useState(PAGE_SIZES[3]);
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState(calendars.slice(0, pageSize));
  const [sortStatus, setSortStatus] = useState({ columnAccessor: "name", direction: "desc" });
  const [selectedRecords, setSelectedRecords] = useState([]);
  const navigate = useNavigate();

  const columnsDef = buildColumnDefinitions();

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  const getter = useMemo(() => {
    const parts = (sortStatus.columnAccessor || "name").split(".");

    return (item) => parts.reduce((acc, key) => (acc == null ? undefined : acc[key]), item);
  }, [sortStatus.columnAccessor]);

  const sortedFull = useMemo(() => {
    const base = calendars || [];
    const sorted = sortBy(getter, base);

    return sortStatus.direction === "desc" ? [...sorted].reverse() : sorted;
  }, [calendars, getter, sortStatus.direction]);

  useEffect(() => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize;

    setRecords(sortedFull.slice(from, to));
  }, [sortedFull, page, pageSize]);

  return (
    <>
      <DataTable
        striped
        highlightOnHover
        withTableBorder
        withColumnBorders
        withRowBorders={false}
        borderRadius="md"
        paginationActiveBackgroundColor={COLORS.DARK_BROWN}
        columns={columnsDef}
        records={records}
        selectedRecords={selectedRecords}
        onSelectedRecordsChange={setSelectedRecords}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        onRowClick={({ record, event }) => handleRowClick({ record, event, navigate })}
        customRowAttributes={(record) => ({
          onMouseDown: (e) => handleRowMouseDown({ record, event: e }),
        })}
        totalRecords={tableFetch?.data?.filteredCount || 0}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={(page) => setPage(page)}
        recordsPerPageOptions={PAGE_SIZES}
        onRecordsPerPageChange={setPageSize}
        renderPagination={({ state, actions, Controls }) => (
          <DataTablePaginationSection
            state={state}
            actions={actions}
            Controls={Controls}
            page={page}
            pageSizeOptions={PAGE_SIZES}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        )}
      />
      <BulkActionsToolbar selectedRecords={selectedRecords} onClearSelection={() => setSelectedRecords([])} />
    </>
  );
}

function handleRowClick({ record, event, navigate }) {
  const interactiveEvent = event.target.closest('button, a, input, textarea, select, [role="button"]');

  if (interactiveEvent) {
    return;
  }

  navigate(`/calendars/${record.id}`);
}

function handleRowMouseDown({ record, event }) {
  if (event.button !== 1) {
    return;
  }

  const interactiveEvent = event.target.closest('button, a, input, textarea, select, [role="button"]');

  if (interactiveEvent) {
    return;
  }

  event.preventDefault();
  window.open(`/calendars/${record.id}`, "_blank");
}

function buildColumnDefinitions() {
  const { staffProfile } = useAuth();
  const { dropdownFetch } = useLocalAuthorityContext();
  const localAuthorities = dropdownFetch?.data ?? [];

  return [
    { accessor: "name", sortable: true },
    { accessor: "type", sortable: true, render: ({ type }) => getLabelById(CALENDAR_TYPES, type) },
    {
      accessor: "startDate",
      title: "Start Date",
      sortable: true,
      render: ({ startDate }) => dateTimeToString(startDate, "dd/MM/yyyy"),
    },
    {
      accessor: "endDate",
      title: "End Date",
      sortable: true,
      render: ({ endDate }) => dateTimeToString(endDate, "dd/MM/yyyy"),
    },
    {
      accessor: "isActive",
      title: "Active",
      render: ({ isActive }) =>
        isActive ? <IconCircleCheckFilled color="green" /> : <IconCircleXFilled color="red" />,
    },
    {
      accessor: "localAuthorityId",
      title: "Local Authority",
      sortable: true,
      render: ({ localAuthorityId }) => localAuthorities.find((x) => x.id === localAuthorityId)?.name,
    },
    {
      accessor: "updatedAt",
      title: "Last Updated",
      sortable: true,
      render: ({ metadata }) => dateTimeToString(metadata.updatedAt, "dd/MM/yyyy HH:mm"),
    },
    {
      accessor: "actions",
      width: "0%",
      render: ({ id }) => (
        <Group gap="xs" wrap="nowrap">
          <CopyIdButton value={id} />
          <DeleteButton calendarId={id} staffProfile={staffProfile} />
        </Group>
      ),
    },
  ];
}

function DeleteButton({ calendarId, staffProfile }) {
  const [opened, { open, close }] = useDisclosure(false);
  const canDelete = useMemo(() => canDeleteCalendar(staffProfile), [staffProfile]);

  return (
    <>
      <Tooltip withArrow position="left" label={`Delete calendar: ${calendarId}`} color="red">
        <ActionIcon
          variant="subtle"
          color="red"
          disabled={!canDelete || opened}
          onClick={(e) => {
            e.stopPropagation();
            open();
          }}
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Tooltip>
      <Modal opened={opened} onClose={close} centered transitionProps={{ transition: "fade", duration: 200 }} size="xl">
        <CalendarDeletionDialog calendarIds={[calendarId]} />
      </Modal>
    </>
  );
}
